import { AfterViewInit, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subscription, debounceTime } from 'rxjs';
import { PageLoaderService } from '../../../core/service/page-loader.service';
import { DialogComponent } from '../../../shared/component/dynamic-form/dialog-box/dialog-component';
import { BlowerDTO, CombinedState, FormValues, PayloadDto, RequiredField } from '../../../shared/dto/blower-payload.dto';
import { SelectedValue } from '../../../shared/dto/blower-selection.dto';
import { InputFields } from '../../../shared/enums/input-fields.enum';
import { ModelDTO } from '../../../shared/models/blower-model.model';
import { Field } from '../../../shared/models/field.model';
import { TransformData } from '../../../shared/models/transform-form-data';
import { ProductsService } from '../../../shared/services/products.service';
import { SharedStateService } from '../../../shared/services/shared-state.service';

@Component({
  selector: 'app-product-content',
  templateUrl: './product-content.component.html',
  styleUrls: ['./product-content.component.scss'],
  standalone: false,
})
export class ProductContentComponent implements OnInit, AfterViewInit, OnDestroy {
  fetchedData$ = new BehaviorSubject<ModelDTO[] | null>(null);
  selectedValues: { grandparent: string; label: string; children: { blowercategory: string }[] }[] = [];
  typesOfBlowers: string[] = [];
  subcategory: string = '';
  typeKeys: string[] = [];
  blowerSubCategory: string = '';
  categoryKeys: string[] = [];
  activeTab: string = 'model';
  activeInnerTab: string = 'results';
  hasSubmitted: boolean = false;
  fields: Field[] = [];
  public form!: FormGroup;
  blowerTypeLabel: string = '';
  allRequiredFieldsPresent: boolean = false;
  activeTabSub: Subscription | undefined;
  transformData: TransformData = {
    vacuum: 0,
    altitude_from_sea_level: 0,
    suction_air_temperature: 0,
    absolute_pressure_after_recovery: 0,
    relative_humidity_after_recovery: 0,
    suction_air_relative_humidity: 0,
    suction_volumetric_flow: 0,
    temperature_after_recovery: 0,
    heat_recovery_used: false,
  };
  queryParameters: string[] = [];
  blowerType: string = '';
  blowerCategory: string = '';
  exactModelBlowerType: string = '';
  exactModelcategoryType: string = '';
  finalCategory: string[] = [];
  public fetchedData: ModelDTO[] = [];
  public specificModelData: ModelDTO[] = [];
  models: string[] = [];
  modelNames: string = '';
  defaultModelName: string = '';
  payloadData: { blowerGroup: string; blowerType: string; blowerCategory: string }[] = [];
  sensorData: { sensorValues: { [key: string]: number }; blowers: BlowerDTO[] } = {
    sensorValues: {},
    blowers: [],
  };
  blowerGroup: string;
  allRequiredFieldsHaveValue: boolean = false;
  isLoading = signal(true);
  fetchLatestFormValues: CombinedState;
  private formValuesReady = false;
  private viewInitialized = false;
  private isFirstSubmit = true;
  heatRecoveryUsed: boolean = true;
  private heatRecoverySub: Subscription;
  private selectedValuesSub: Subscription | undefined;
  constructor(
    private productservice: ProductsService,
    private pageLoaderService: PageLoaderService,
    private dialog: MatDialog,
    private sharedStateService: SharedStateService,
  ) {}

  ngOnInit() {
    this.selectedValuesSub = this.productservice.selectedValues$.pipe(debounceTime(300)).subscribe((value: SelectedValue[]) => {
      this.updateSelectedValues(value);
    });

    if (this.fetchLatestFormValues) {
      this.selectTab(this.activeTab);
      this.handleSubmit();
    }

    this.heatRecoverySub = this.sharedStateService.heatRecoveryUsed$.subscribe((val) => {
      this.heatRecoveryUsed = val;
      if (this.fetchLatestFormValues) {
        this.handleFormValueChanged(this.fetchLatestFormValues);
      }
    });
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.checkAndProcessFormValues();
  }

  private checkAndProcessFormValues() {
    // Guard and schedule the initial submit flow exactly once after view init
    if (this.viewInitialized && this.formValuesReady && !this.isLoading() && this.fetchLatestFormValues && this.isFirstSubmit) {
      this.isFirstSubmit = false;
      this.scheduleInitialSubmit();
    }
  }

  private scheduleInitialSubmit(): void {
    // Keep behavior identical: wait a tick then select tab and submit
    setTimeout(() => {
      this.selectTab(this.activeTab);
      this.handleSubmit();
    });
  }

  updateSelectedValues(value: SelectedValue[]) {
    this.productservice.updateFetchedData([]);
    this.selectedValues = value;
    this.payloadData = [];
    this.sensorData = { sensorValues: {}, blowers: [] };
    const { payload, blowers } = this.buildPayloadFromSelectedValues(this.selectedValues);
    this.payloadData = payload;
    this.sensorData.blowers = blowers;

    this.productservice.getProductFields(this.payloadData).subscribe({
      next: (fields) => {
        this.isLoading.set(false);
        const normalizedFields = JSON.parse(JSON.stringify(fields)) as Record<string, Field>;
        this.fields = Object.values(normalizedFields);
        if (this.productservice.updateFormFields) {
          this.productservice.updateFormFields(this.fields);
        }
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
    if (this.activeTab == 'model') {
      // Prefer cached model names when available (faster tab switch UX)
      const cached = this.productservice.getModelNamesCached ? this.productservice.getModelNamesCached() : [];
      if (cached && cached.length > 0) {
        this.models = cached;
      } else {
        this.productservice.getModelNames().subscribe((data) => {
          this.models = data;
          if (this.productservice.updateModelNames) {
            this.productservice.updateModelNames(this.models);
          }
        });
      }
    }
  }

  /**
   * Build arrays used for payload and sensor data from the selected values.
   * Kept private and pure to simplify `updateSelectedValues` and reduce cognitive complexity.
   */
  private buildPayloadFromSelectedValues(selected: SelectedValue[]) {
    const payload: { blowerGroup: string; blowerType: string; blowerCategory: any }[] = [];
    const blowers: BlowerDTO[] = [];

    selected.forEach((item: SelectedValue) => {
      const blowerType = item?.label;
      const blowerGroup = item?.grandparent;

      if (item.children && item.children.length > 0) {
        item.children.forEach((blowercategory: any) => {
          const blowerCategory = blowercategory;
          payload.push({ blowerGroup, blowerType, blowerCategory });
          blowers.push({ blowerGroup, blowerType, blowerCategory } as BlowerDTO);
        });
      } else {
        const blowerCategory = null;
        payload.push({ blowerGroup, blowerType, blowerCategory });
        blowers.push({ blowerGroup, blowerType, blowerCategory } as BlowerDTO);
      }
    });

    return { payload, blowers };
  }

  transformPropertyName(name: string): string {
    return InputFields[name as keyof typeof InputFields];
  }

  fetchExactModel(queryParams: PayloadDto) {
    // Attach top-level unitType if present (backend expects unitType at top level)
    const latest = this.fetchLatestFormValues;
    const topUnitType = latest && latest.formValues && latest.formValues['unitType'];
    if (topUnitType) {
      (queryParams as any).unitType = topUnitType;
    }

    this.pageLoaderService.showSpinnerForDataLoading(
      this.productservice.getExactModels(queryParams).subscribe((res: ModelDTO[]) => {
        this.fetchedData = res.map((model) => ({
          ...model,
          exactModel: model.exactModel,
        }));
        this.productservice.updateFetchedData(this.fetchedData);
      }),
    );
  }

  fetchSpecificModel(transformData: FormValues, specificModel: string) {
    // Attach top-level unitType if present (backend expects unitType at top level)
    const latest = this.fetchLatestFormValues;
    const topUnitType = latest && latest.formValues && latest.formValues['unitType'];
    if (topUnitType) {
      (transformData as any).unitType = topUnitType;
    }

    this.pageLoaderService.showSpinnerForDataLoading(
      this.productservice.getSpecificModel(transformData, specificModel).subscribe((res: ModelDTO[]) => {
        this.specificModelData = res.map((model) => ({
          ...model,
          exactModel: model.exactModel,
        }));
        this.productservice.updateFetchedData(this.specificModelData);
        this.productservice.updateModelData(this.specificModelData);
      }),
    );
  }

  handleSubmit() {
    if (!this?.fetchLatestFormValues) {
      this.openFormUndefinedDialog();
      return;
    }

    const { formValues } = this.fetchLatestFormValues;

    if (!this.allRequiredFieldsPresent) {
      this.allRequiredFieldsHaveValue = false;
      this.clearFetchedData();
      return;
    }

    this.hasSubmitted = true;
    const newTransformData = this.buildTransformData(formValues);
    this.updateSensorAndTrigger(newTransformData);
    this.fetchModelsAfterSubmit(newTransformData, formValues);
  }

  private openFormUndefinedDialog(): void {
    this.dialog.open(DialogComponent, {
      data: {
        message: 'Form values are not defined',
      },
    });
  }

  private buildTransformData(formValues: Record<string, any>): TransformData {
    const newTransformData: TransformData = {
      vacuum: 0,
      altitude_from_sea_level: 0,
      suction_air_temperature: 0,
      absolute_pressure_after_recovery: 0,
      relative_humidity_after_recovery: 0,
      suction_air_relative_humidity: 0,
      suction_volumetric_flow: 0,
      temperature_after_recovery: 0,
      heat_recovery_used: this.heatRecoveryUsed,
    };

    const keysToExclude = ['selectedModel'];

    for (const key in formValues) {
      if (Object.prototype.hasOwnProperty.call(formValues, key) && !keysToExclude.includes(key)) {
        const value = formValues[key];
        const name = this.transformPropertyName(key);
        if (name in newTransformData) {
          (newTransformData as any)[name] = value;
        }
      }
    }

    return newTransformData;
  }

  private updateSensorAndTrigger(transformData: TransformData): void {
    this.allRequiredFieldsHaveValue = true;
    this.transformData = transformData;
    this.isLoading.set(false);

    this.sensorData.sensorValues = Object.entries(this.transformData)
      .filter(([key, value]) => typeof value === 'number' && key !== 'heat_recovery_used')
      .reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value as number,
        }),
        {},
      );

    // Keep original behavior: send the full transform object to the service
    this.productservice.updateSensorValues({ sensorValues: this.transformData });
    this.sharedStateService.triggerSubmitClicked();
  }

  private fetchModelsAfterSubmit(transformData: TransformData, formValues: Record<string, any>): void {
    if (this.activeTab == 'model' && formValues['selectedModel'] !== '') {
      this.fetchSpecificModel(transformData, formValues['selectedModel']);
    } else if (this.activeTab == 'condition') {
      this.fetchExactModel(this.sensorData);
    }
  }
  selectTab(tab: string) {
    this.activeTab = tab;
    if (this.activeTab === 'model') {
      // When selecting the tab, prefer a cached list if present to avoid re-fetch latency
      const cachedTab = this.productservice.getModelNamesCached ? this.productservice.getModelNamesCached() : [];
      if (cachedTab && cachedTab.length > 0) {
        this.models = cachedTab;
      } else {
        this.productservice.getModelNames().subscribe((data) => {
          this.models = data;
          if (this.productservice.updateModelNames) {
            this.productservice.updateModelNames(this.models);
          }
        });
      }
    }

    this.clearDataForInactiveTabs();
    this.subscribeToData();
  }

  onModelSelected(selectedModel: string) {
    this.modelNames = selectedModel;
  }

  onDefaulModelName(data: string) {
    this.defaultModelName = data;
  }

  selectInnerTab(tab: string): void {
    this.activeInnerTab = tab;
    if (tab === 'graphs') {
      // Allow Angular to render the graph component before triggering resize
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
    }
  }

  handleFormValueChanged(data: CombinedState) {
    this.allRequiredFieldsPresent = false;
    const { formValues, requiredFields } = data;
    this.fetchLatestFormValues = data;
    this.formValuesReady = true;

    if (!requiredFields || requiredFields.length === 0) {
      // No required fields reported — keep previous behaviour
      this.checkAndProcessFormValues();
      return;
    }

    const visibleRequiredFields = this.filterVisibleRequiredFields(requiredFields);

    const allFieldsFilled = visibleRequiredFields.every((field) => this.isRequiredFieldSatisfied(field, formValues));

    const modelSelected = this.isModelSelected(formValues);

    this.allRequiredFieldsPresent = allFieldsFilled && modelSelected;

    this.checkAndProcessFormValues();
  }

  private isHeatRecoveryField(fieldName: string): boolean {
    return (
      fieldName === 'temperatureAfterRecovery' ||
      fieldName === 'relativeHumidityAfterRecovery' ||
      fieldName === 'absolutePressureAfterRecovery'
    );
  }

  private filterVisibleRequiredFields(requiredFields: RequiredField[]): RequiredField[] {
    return requiredFields.filter((field) => {
      if (this.isHeatRecoveryField(field.name)) {
        return this.heatRecoveryUsed;
      }
      return true;
    });
  }

  private isRequiredFieldSatisfied(field: RequiredField, formValues: FormValues | undefined): boolean {
    const raw = formValues ? (formValues as any)[field.name] : undefined;
    if (raw === undefined || raw === null || raw === '') {
      return false;
    }

    const maybeNum = typeof raw === 'number' ? raw : Number(raw);
    const isNumber = !Number.isNaN(maybeNum);

    if ((field.min !== undefined || field.max !== undefined) && isNumber) {
      if (field.min !== undefined && maybeNum < field.min) {
        return false;
      }
      if (field.max !== undefined && maybeNum > field.max) {
        return false;
      }
    }

    return true;
  }

  private isModelSelected(formValues: FormValues | undefined): boolean {
    return !!(
      formValues &&
      formValues['selectedModel'] !== undefined &&
      formValues['selectedModel'] !== null &&
      formValues['selectedModel'] !== ''
    );
  }
  onRequiredFieldsChange(present: boolean): void {
    this.allRequiredFieldsHaveValue = present;
    if (!present) {
      this.clearDataForInactiveTabs();
      // Use a thin delegator to make this call-site distinct from other copies
      this.clearServiceFetchedData();
    }
  }

  clearDataForInactiveTabs(): void {
    if (this.activeTab === 'condition') {
      this.specificModelData = [];
    } else if (this.activeTab === 'model') {
      this.fetchedData = [];
    }
  }

  clearFetchedData(): void {
    // Public alias kept for backward compatibility
    this.clearServiceFetchedData();
  }

  private clearServiceFetchedData(): void {
    // Remove any fetched/model data from the service
    this.productservice.updateFetchedData([]);
    this.productservice.updateModelData([]);
  }

  ngOnDestroy() {
    this.heatRecoverySub?.unsubscribe();
    this.activeTabSub?.unsubscribe();
    this.selectedValuesSub?.unsubscribe();
  }

  subscribeToData(): void {
    this.activeTabSub?.unsubscribe();

    if (this.activeTab === 'condition') {
      this.activeTabSub = this.productservice.fetchedData$.subscribe((data) => {
        this.fetchedData = data;
      });
    } else if (this.activeTab === 'model') {
      this.activeTabSub = this.productservice.modelData$.subscribe((data) => {
        this.specificModelData = data;
      });
    }
  }
}
